/**
 * SplinkCard — always-visible short-URL + QR badge for shareable pages.
 *
 * Different from <ShareButton /> in that it renders the QR and short
 * URL directly on the page (no click needed) — designed for printed
 * menus, business cards, family-tree gift prints, memorial keepsake
 * cards, etc.
 *
 * Behaviour:
 *   • Mounts → immediately POSTs /api/splink/shorten with the current
 *     page URL, current site slug and a human-readable label.
 *   • Anonymous users see a copy-only fallback (Splink is auth-scoped).
 *   • Falls back gracefully to a "long URL copy" strip if the Splink
 *     API is unreachable — the card never renders broken.
 *   • Response is cached in-memory per tab so remounts are free.
 *
 * Deliberately compact (fits in a sidebar or below-the-fold slot) but
 * scales to print size when the download-SVG button is used.
 */
import { useEffect, useState } from "react";
import { Copy, Check, ExternalLink, Download, QrCode } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { resolveSiteSlug } from "@/config/siteConfig";

// Session-scoped cache so multiple SplinkCards on the same page (or
// nav-out-and-back) don't re-mint the same URL.
const CACHE = new Map();

export default function SplinkCard({
  url,           // optional — defaults to window.location.href at mount time
  label,         // shown in the user's Splink dashboard as the link's name
  headline,      // small heading over the card (e.g. "Share this tree")
  className = "",
  testId = "splink-card",
}) {
  const { user } = useAuth();
  const [data, setData] = useState(null);        // {short_url, qr_code_url, id, slug}
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const target = url || (typeof window !== "undefined" ? window.location.href : "");

  useEffect(() => {
    let cancelled = false;
    if (!target) return;
    const cached = CACHE.get(target);
    if (cached) { setData(cached); return; }

    (async () => {
      setBusy(true);
      try {
        const site = resolveSiteSlug();
        const res = await http.post("/splink/shorten", { long_url: target, label, site });
        if (cancelled) return;
        CACHE.set(target, res.data);
        setData(res.data);
      } catch (e) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => { cancelled = true; };
  }, [target, label, user]);

  const shortUrl = data?.short_url;
  const qrUrl    = data?.qr_code_url;
  const shownUrl = shortUrl || target;

  function copy() {
    try {
      navigator.clipboard.writeText(shownUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  }

  // Anonymous but not-yet-loaded users show the fallback strip
  // (no QR) until the mint completes. Failed mints stay on fallback.
  const showFallback = failed || (!busy && !data);

  return (
    <div
      className={`rounded-2xl border p-4 sm:p-5 ${className}`}
      style={{ borderColor: "#E9D8B4", background: "#FFFDF8" }}
      data-testid={testId}
    >
      {headline && (
        <div className="flex items-center gap-2 mb-3">
          <QrCode className="w-4 h-4" style={{ color: "#B25D38" }} aria-hidden />
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: "#8B5E10" }}
          >
            {headline}
          </div>
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* QR square (real QR when signed in, placeholder when not). */}
        <div
          className="shrink-0 rounded-lg border overflow-hidden bg-white p-1"
          style={{ borderColor: "#E9D8B4", width: 96, height: 96 }}
          data-testid={`${testId}-qr-wrap`}
        >
          {qrUrl ? (
            <img
              src={qrUrl}
              alt="QR code"
              width={88}
              height={88}
              className="block"
              data-testid={`${testId}-qr`}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-center text-[9px] leading-tight"
              style={{ color: "#8B5E10", background: "#F3E7C6" }}
            >
              {busy ? "Minting…" : "QR unavailable"}
            </div>
          )}
        </div>

        {/* URL + actions */}
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] font-mono uppercase tracking-widest mb-1"
            style={{ color: "#B25D38" }}
          >
            {shortUrl ? "Short link" : "Direct link"}
          </div>
          <a
            href={shownUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block font-mono text-sm truncate hover:underline"
            style={{ color: "#1A1613" }}
            data-testid={`${testId}-url`}
          >
            {shownUrl.replace(/^https?:\/\//, "")}
          </a>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold border transition-shadow hover:shadow-sm"
              style={{ background: "#8B5E10", color: "#fff", borderColor: "#8B5E10" }}
              data-testid={`${testId}-copy`}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <a
              href={shownUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold border hover:shadow-sm transition-shadow"
              style={{ background: "#FFFFFF", color: "#8B5E10", borderColor: "#C79A3F" }}
              data-testid={`${testId}-open`}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Open
            </a>
            {qrUrl && (
              <a
                href={`${qrUrl}${qrUrl.includes("?") ? "&" : "?"}format=svg`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold border hover:shadow-sm transition-shadow"
                style={{ background: "#FFFFFF", color: "#8B5E10", borderColor: "#C79A3F" }}
                title="Download SVG for print"
                data-testid={`${testId}-download`}
              >
                <Download className="w-3.5 h-3.5" /> SVG
              </a>
            )}
          </div>
          {showFallback && failed && (
            <div className="mt-2 text-[10px] opacity-70" style={{ color: "#3E2A0A" }}>
              Short-link service unavailable. You can still copy the direct link above.
            </div>
          )}
        </div>
      </div>

      <div
        className="mt-3 text-[9px] font-mono uppercase tracking-widest text-right opacity-60"
        style={{ color: "#8B5E10" }}
      >
        Powered by SpLink
      </div>
    </div>
  );
}
